import { FC, useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { oportunidadeService } from '@/services/oportunidadeService'
import { curadoriaService } from '@/services/curadoriaService'
import { ReservasResumo } from '@/types/curadoria'
import { clienteService } from '@/services/clienteService'
import { documentoService } from '@/services/documentoService'
import { usuarioService } from '@/services/usuarioService'
import { renovacaoService } from '@/services/renovacaoService'
import { useAuth } from '@/hooks/useAuth'
import { Documento } from '@/types/documento'
import { ReuniaoDialog } from '@/components/agenda/ReuniaoDialog'
import { DocsObrigatoriosDialog } from '@/components/reservas/DocsObrigatoriosDialog'
import { formatCNPJ, formatDate } from '@/utils/formatters'
import { cnpjContemTermo } from '@/utils/cnpj'
import EventIcon from '@mui/icons-material/Event'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import Tooltip from '@mui/material/Tooltip'
import {
  Box, Typography, Button, Paper, Grid, Chip, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert,
  IconButton, List, ListItem, ListItemIcon, ListItemText, Badge,
  ListItemSecondaryAction, Divider, CircularProgress, MenuItem, Menu, Checkbox,
} from '@mui/material'
import { baixarCsv, sufixoData } from '@/utils/csv'
import {
  EtapaComercial, ETAPAS_COMERCIAIS, ETAPA_COMERCIAL_LABEL, etapaComercialChipColor,
  MOTIVO_AGENDAMENTO_INDISPONIVEL, permiteAgendamento,
} from '@/types/statusReserva'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import DescriptionIcon from '@mui/icons-material/Description'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { tokens } from '@/theme'


interface ReservaRow {
  id: string
  numero?: number
  clienteId?: string
  data: string
  /** Última movimentação (`updatedAt` da reserva, senão a criação), já formatada. */
  ultimaMovimentacao: string
  empresa: string
  cnpj: string
  consultorId?: string
  consultor: string
  produto: string
  valor: number
  etapa: string
  status: string
  /** Prazo de fechamento (ISO YYYY-MM-DD) ou null quando não definido. */
  prazoReserva: string | null
  /** Prazo de operacionalização (assinatura + 60d); só existe depois de assinado. */
  prazoOperacionalizacao: string | null
  /** Status real (18 estados) — define qual dos dois prazos governa a reserva. */
  statusReserva: string | null
  /** Nulo = renovação automática (+30, única) ainda disponível — define o modo do diálogo. */
  renovacaoAutomaticaUsadaEm: string | null
  /** Score de aderência (0–100) calculado pelo backend — substituiu a probabilidade digitada. */
  aderencia: number
  /** "O que fazer a seguir" (Fase 6, item 18) — persistido na reserva. */
  proximaAcao: string | null
  /** Etapa comercial (6.4), derivada no backend. Nula com backend anterior → cai no legado. */
  etapaComercial: EtapaComercial | null
  docsCount: number
  obrigatoriosFaltantes: number
  obrigatoriosTotal: number
  /** Back-office pediu a reunião com o cliente (entrega G); nulo = sem pedido. */
  reuniaoSolicitadaEm: string | null
  reuniaoSolicitadaObservacao: string | null
  /** Primeiro contato (reunião realizada) — encerra o destaque do pedido. */
  dataPrimeiroContato: string | null
}

/**
 * Pedido de reunião da Originei ainda em aberto: solicitado, sem reunião realizada e com a
 * reserva viva (encerrada/cancelada não tem mais reunião a agendar — revisão de 16/10/2026).
 */
const reuniaoSolicitadaPendente = (r: ReservaRow): boolean =>
  !!r.reuniaoSolicitadaEm && !r.dataPrimeiroContato && permiteAgendamento(r.statusReserva)

/** "Solicitada em dd/mm/aaaa — observação" (tooltip do destaque). */
const textoSolicitacaoReuniao = (r: ReservaRow): string => {
  const data = r.reuniaoSolicitadaEm ? new Date(r.reuniaoSolicitadaEm).toLocaleDateString('pt-BR') : '—'
  return `Solicitada em ${data}${r.reuniaoSolicitadaObservacao ? ` — ${r.reuniaoSolicitadaObservacao}` : ''}`
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'

/** Status pós-assinatura em que o prazo de operacionalização é o que vale. */
const STATUS_EM_OPERACIONALIZACAO = ['CONTRATO_ASSINADO', 'EM_OPERACIONALIZACAO']

/**
 * Qual dos dois prazos de 60 dias governa a reserva agora — e como rotulá-lo.
 *
 * Antes da assinatura vale o prazo de fechamento; entre CONTRATO_ASSINADO e ATIVO
 * passa a valer o de operacionalização. Depois de ATIVO (ou nos estados terminais)
 * nenhum dos dois gera alerta: a reserva já chegou onde precisava.
 */
const prazoVigente = (
  r: Pick<ReservaRow, 'prazoReserva' | 'prazoOperacionalizacao' | 'statusReserva' | 'etapa'>,
): { prazoISO: string | null; operacionalizacao: boolean; alertavel: boolean } => {
  const status = r.statusReserva
  if (status && STATUS_EM_OPERACIONALIZACAO.includes(status)) {
    return { prazoISO: r.prazoOperacionalizacao, operacionalizacao: true, alertavel: true }
  }
  // Sem status (reserva antiga/payload sem o campo) caímos no comportamento anterior:
  // "Etapa 3" = contratada, que não alerta.
  const contratada = status ? !STATUS_EM_OPERACIONALIZACAO.includes(status) && r.etapa === 'Etapa 3'
                            : r.etapa === 'Etapa 3'
  return { prazoISO: r.prazoReserva, operacionalizacao: false, alertavel: !contratada }
}

/**
 * Situação do prazo vigente para exibição no grid. Alinhado aos jobs de alerta do
 * backend (janela de 7 dias, inclui vencidos).
 */
const prazoInfo = (
  prazoISO: string | null,
  semAlerta: boolean,
  operacionalizacao = false,
): { label: string; color: ChipColor; tooltip: string } => {
  const oQue = operacionalizacao ? 'Operacionalização' : 'Reserva'
  if (!prazoISO) return { label: '—', color: 'default', tooltip: 'Sem prazo definido' }
  const data = formatDate(prazoISO)
  if (semAlerta) return { label: data, color: 'default', tooltip: 'Reserva contratada' }
  // Diferença em dias inteiros entre o prazo e hoje (ambos à meia-noite local).
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const prazo = new Date(`${prazoISO}T00:00:00`)
  const dias = Math.round((prazo.getTime() - hoje.getTime()) / 86_400_000)
  const prefixo = operacionalizacao ? 'Operacionalização: ' : ''
  if (dias < 0) {
    return {
      label: data,
      color: 'error',
      tooltip: `${oQue} vencida há ${Math.abs(dias)} dia(s)`,
    }
  }
  if (dias === 0) return { label: data, color: 'error', tooltip: `${prefixo}vence hoje` }
  if (dias <= 7) return { label: data, color: 'warning', tooltip: `${prefixo}vence em ${dias} dia(s)` }
  return { label: data, color: 'default', tooltip: `${prefixo}vence em ${dias} dia(s)` }
}

/** Dias inteiros até o prazo. Negativo = vencida; `null` quando não há prazo. */
const diasAteOPrazo = (prazoISO: string | null): number | null => {
  if (!prazoISO) return null
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  return Math.round((new Date(`${prazoISO}T00:00:00`).getTime() - hoje.getTime()) / 86_400_000)
}

/** Últimos dias em que a ação Renovar é oferecida (RES-003 do Handoff v3.0). */
const JANELA_RENOVACAO_DIAS = 5

/** Teto de dias que uma renovação concede (RES-004) — espelha `RegrasRenovacao` no backend. */
const MAX_DIAS_RENOVACAO = 60

/**
 * Filtro de vigência (Fase 6, item 19). Classifica pelo prazo que GOVERNA a reserva
 * (fechamento ou operacionalização — o mesmo `prazoVigente` do grid e dos alertas), com
 * a mesma janela de 5 dias da renovação: o consultor filtra "próximas do vencimento" e
 * vê exatamente as reservas em que o botão Renovar já aparece.
 *
 * Reserva sem prazo ou já contratada (prazo não alertável) só aparece em "Todas" —
 * afirmar que ela está "ativa" ou "vencida" seria inventar vigência onde não há.
 */
export type FiltroVigencia = 'todas' | 'ativas' | 'vencendo' | 'vencidas'

export const classificarVigencia = (
  r: Pick<ReservaRow, 'prazoReserva' | 'prazoOperacionalizacao' | 'statusReserva' | 'etapa'>,
): Exclude<FiltroVigencia, 'todas'> | null => {
  const { prazoISO, alertavel } = prazoVigente(r)
  const dias = diasAteOPrazo(prazoISO)
  if (dias === null || !alertavel) return null
  if (dias < 0) return 'vencidas'
  if (dias <= JANELA_RENOVACAO_DIAS) return 'vencendo'
  return 'ativas'
}

const ROTULO_VIGENCIA: Record<FiltroVigencia, string> = {
  todas: 'Todas as situações',
  ativas: `Ativas · mais de ${JANELA_RENOVACAO_DIAS} dias`,
  vencendo: `Próximas do vencimento · até ${JANELA_RENOVACAO_DIAS} dias`,
  vencidas: 'Vencidas',
}

/**
 * Data máxima selecionável na renovação: 60 dias além do prazo vigente (ou de hoje, quando
 * a reserva não tem prazo). Devolve `null` quando não há reserva em foco.
 */
const limiteRenovacao = (
  r: Pick<ReservaRow, 'prazoReserva' | 'prazoOperacionalizacao' | 'statusReserva' | 'etapa'> | null,
): string | null => {
  if (!r) return null
  const base = prazoVigente(r).prazoISO
  const dataBase = base ? new Date(`${base}T00:00:00`) : new Date()
  dataBase.setDate(dataBase.getDate() + MAX_DIAS_RENOVACAO)
  return dataBase.toISOString().slice(0, 10)
}

/**
 * A ação Renovar aparece nos últimos dias de vigência, não a qualquer momento — RES-003,
 * confirmado pelo negócio em 14/08. O handoff enuncia a regra como a ação *aparecer*, então
 * ela mora aqui, na tela, e não como proibição no servidor.
 *
 * Três faixas, e a terceira é deliberada:
 *  - falta mais que a janela → não oferece (renovar cedo demais esvazia o prazo);
 *  - dentro da janela        → oferece a todos (consultor solicita, back-office estende);
 *  - vencendo hoje ou já vencida → oferece **só ao back-office**. A política de reativação de
 *    reserva vencida continua **em aberto** no handoff; até existir, preservamos a capacidade
 *    atual de quem tem autoridade em vez de criar um beco sem saída na tela.
 *
 * Sem prazo definido, não há janela a aplicar: mantém o comportamento anterior.
 */
export const ofereceRenovacao = (
  prazoISO: string | null,
  ehBackOffice: boolean,
): boolean => {
  const dias = diasAteOPrazo(prazoISO)
  if (dias === null) return true
  if (dias > JANELA_RENOVACAO_DIAS) return false
  if (dias >= 1) return true
  return ehBackOffice
}

/**
 * Renovar prazo é ato da Originei: o consultor solicita por fora, quem estende
 * é ADMIN/GERENTE/OPERADOR. Espelha o @PreAuthorize do endpoint — o backend
 * continua sendo a autoridade; aqui só evitamos oferecer um botão que dará 403.
 */
const ROLES_RENOVACAO = ['ADMIN', 'GERENTE', 'OPERADOR']

/**
 * Mover reserva entre carteiras é gestão de carteira, não operação do dono dela —
 * por isso ADMIN/GERENTE apenas (espelha o @PreAuthorize; o backend é a autoridade).
 */
const ROLES_TRANSFERENCIA = ['ADMIN', 'GERENTE']

const ESTAGIO_META: Record<string, { etapa: string; status: string }> = {
  DIAGNOSTICO:  { etapa: 'Em análise',  status: 'Aguardando' },
  ESTRUTURACAO: { etapa: 'Etapa 1',     status: 'Em estruturação' },
  NEGOCIACAO:   { etapa: 'Etapa 2',     status: 'Em negociação' },
  CONTRATADA:   { etapa: 'Etapa 3',     status: 'Contratada' },
}

/** Filtro "Situação" do Figma = etapa comercial (6.4), nunca o status gravado. */
type FiltroSituacao = 'TODAS' | EtapaComercial

/**
 * Colunas fora das cinco do Figma (Empresa/CNPJ · Produto · Situação · Última movimentação ·
 * Validade). Ficam no menu `⋯`, junto do CSV — a grid antiga tinha 13 e ninguém lia.
 */
type ColunaExtra = 'consultor' | 'valor' | 'docs' | 'aderencia' | 'criadaEm'
const COLUNAS_EXTRAS: { id: ColunaExtra; rotulo: string }[] = [
  { id: 'consultor', rotulo: 'Consultor' },
  { id: 'valor', rotulo: 'Valor' },
  { id: 'docs', rotulo: 'Documentos' },
  { id: 'aderencia', rotulo: 'Aderência' },
  { id: 'criadaEm', rotulo: 'Criada em' },
]

/** Formata o número da reserva: 1 → "RES-0001". */
const formatNumeroReserva = (numero?: number) =>
  numero != null ? `RES-${String(numero).padStart(4, '0')}` : '—'

const statusColor = (status: string) => {
  if (status.includes('Aprovado') || status.includes('operacao')) return 'success'
  if (status.includes('Pendencia')) return 'error'
  if (status.includes('Reuniao') || status.includes('agendada')) return 'warning'
  return 'default'
}

const etapaColor = (etapa: string) => {
  if (etapa.includes('analise')) return 'default'
  if (etapa === 'Etapa 1') return 'info'
  if (etapa === 'Etapa 2') return 'secondary'
  if (etapa === 'Etapa 3') return 'success'
  return 'default'
}

// O CATÁLOGO de documentos obrigatórios vem do backend (mesma regra que bloqueia o
// envio); aqui só resta a constante de tipo de entidade.
import {
  DocumentoObrigatorio,
  TIPO_ENTIDADE_OPORTUNIDADE,
} from '@/utils/docsObrigatorios'

const ReservasListPage: FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { usuario } = useAuth()
  const [search, setSearch] = useState('')
  const [vigencia, setVigencia] = useState<FiltroVigencia>('todas')
  const [situacao, setSituacao] = useState<FiltroSituacao>('TODAS')
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  // Estado real
  const [reservas, setReservas] = useState<ReservaRow[]>([])
  const [loading, setLoading] = useState(true)
  const [erroCarregar, setErroCarregar] = useState<string | null>(null)
  const [erroCarregarStatus, setErroCarregarStatus] = useState<number | null>(null)
  /**
   * KPIs (R01 #1): `GET /v1/oportunidades` é paginado no servidor, então contar as linhas
   * carregadas mentiria a partir da 101ª reserva. Os quatro números vêm PRONTOS de
   * `GET /v1/oportunidades/resumo` (regra única do backend, mesmo escopo — Onda 6 / A11,
   * lacuna (d) da Rodada 2); nulo (falha) vira "—" em todos.
   */
  const [resumoReservas, setResumoReservas] = useState<ReservasResumo | null>(null)
  // Agendar reunião a partir de uma reserva (prefill com oportunidade/cliente)
  const [reuniaoDialogOpen, setReuniaoDialogOpen] = useState(false)
  const [reuniaoPrefill, setReuniaoPrefill] = useState<
    { oportunidadeId: string; clienteId?: string; clienteNome?: string; titulo?: string; primeiroContato?: boolean } | undefined
  >()

  // Upload de docs obrigatórios faltantes
  const [docsDialogReserva, setDocsDialogReserva] = useState<ReservaRow | null>(null)

  // Detalhes de uma reserva (abre ao clicar na linha)
  const [detailReserva, setDetailReserva] = useState<ReservaRow | null>(null)
  const [detailDocs, setDetailDocs] = useState<Documento[]>([])
  const [detailLoadingDocs, setDetailLoadingDocs] = useState(false)
  const [detailErroDocs, setDetailErroDocs] = useState<string | null>(null)
  // Documento sendo visualizado/baixado no dialog de detalhes
  const [docAcaoId, setDocAcaoId] = useState<string | null>(null)

  // Diretório de consultores (destino da transferência de carteira).
  const [consultores, setConsultores] = useState<{ id: string; nome: string }[]>([])

  // Transferência de carteira
  const [transferirReserva, setTransferirReserva] = useState<ReservaRow | null>(null)
  const [transferirDestino, setTransferirDestino] = useState('')
  const [transferirMotivo, setTransferirMotivo] = useState('')
  const [transferirErro, setTransferirErro] = useState<string | null>(null)
  const [transferindo, setTransferindo] = useState(false)

  // Renovação de prazo. Dois modos no mesmo diálogo:
  //  - 'direta'     → Originei estende o prazo na hora (10a)
  //  - 'automatica' → primeira renovação do consultor: +30 exatos, aprovada na hora (v4.0)
  //  - 'solicitacao'→ consultor PEDE a extensão e a Originei decide (10b)
  const [renovarReserva, setRenovarReserva] = useState<ReservaRow | null>(null)
  const [renovarModo, setRenovarModo] = useState<'direta' | 'automatica' | 'solicitacao'>('direta')
  const [renovarPrazo, setRenovarPrazo] = useState('')
  const [renovarMotivo, setRenovarMotivo] = useState('')
  const [renovarErro, setRenovarErro] = useState<string | null>(null)
  const [renovando, setRenovando] = useState(false)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErroCarregar(null)
    setErroCarregarStatus(null)
    try {
      // 4 chamadas em paralelo: oportunidades, clientes, documentos vinculados e usuários.
      // Se a de documentos falhar, seguimos com docsCount=0 em vez de quebrar a tela.
      // A de usuários (só p/ resolver nome do consultor) pode falhar por permissão
      // (consultor sem acesso ao diretório) — nesse caso caímos no fallback de nome.
      const [oportunidadesPage, clientesPage, documentosPage, usuarios, catalogo, resumo] = await Promise.all([
        oportunidadeService.listar(0, 100),
        clienteService.listar(0, 200),
        documentoService
          .listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, undefined, 0, 500)
          .catch(() => ({ content: [], totalElements: 0, totalPages: 0, size: 0, number: 0 })),
        usuarioService.listar({ max: 200 }).catch(() => []),
        // Catálogo de documentos obrigatórios: total por produto na coluna "Documentos".
        oportunidadeService
          .documentosObrigatoriosCatalogo()
          .catch(() => ({}) as Record<string, DocumentoObrigatorio[]>),
        // Resumo dos KPIs (regra única do backend); falha aqui vira "—" nos cards, não erro de tela.
        curadoriaService.resumoReservas().catch(() => null),
      ])
      setResumoReservas(resumo)
      const listaClientes = clientesPage.content ?? []
      const clienteById = new Map(listaClientes.map((c) => [c.id, c]))
      const nomeConsultorById = new Map((usuarios ?? []).map((u) => [u.id, u.nome]))
      // O mesmo diretório alimenta o seletor de destino da transferência de carteira. Sem ele
      // (só o ADMIN lê o diretório), valem os consultores com reserva e o nome gravado (V49).
      let destinos = (usuarios ?? []).map((u) => ({ id: u.id, nome: u.nome }))
      if (destinos.length === 0) {
        try {
          destinos = (await oportunidadeService.listarConsultores()).map((c) => ({
            id: c.id,
            nome: c.nome ?? `${c.id.slice(0, 8)}…`,
          }))
        } catch {
          destinos = []
        }
      }
      setConsultores(destinos)
      // Resolve o nome do consultor: o gravado na reserva (V49, 14/10/2026 — funciona para
      // todo perfil) → diretório (só o ADMIN o lê) → o próprio usuário logado → '—'.
      const resolverConsultor = (
        id?: string | null,
        gravado?: string | null,
      ): { id?: string; nome: string } => {
        if (!id) return { nome: gravado ?? '—' }
        const nome =
          gravado ??
          nomeConsultorById.get(id) ??
          (id === usuario?.id ? usuario?.nome ?? 'Você' : '—')
        return { id, nome }
      }

      // Conta TODOS os documentos vinculados à oportunidade (obrigatórios + adicionais)
      // e indexa-os por oportunidade para cálculo de faltantes.
      const docsPorOportunidade = new Map<string, number>()
      const docsListPorOportunidade = new Map<string, Documento[]>()
      for (const d of documentosPage.content ?? []) {
        if (d.tipoEntidade && d.tipoEntidade !== TIPO_ENTIDADE_OPORTUNIDADE) continue
        const ref = d.entidadeReferencia
        if (!ref) continue
        docsPorOportunidade.set(ref, (docsPorOportunidade.get(ref) ?? 0) + 1)
        const arr = docsListPorOportunidade.get(ref) ?? []
        arr.push(d)
        docsListPorOportunidade.set(ref, arr)
      }

      const rows: ReservaRow[] = (oportunidadesPage.content ?? []).map((o) => {
        const cliente = clienteById.get(o.clienteId as unknown as string)
        const meta = ESTAGIO_META[o.estagio as unknown as string] ?? { etapa: o.estagio, status: o.estagio }
        const criadoEm = o.createdAt ? new Date(o.createdAt) : null
        const movidoEm = o.updatedAt ? new Date(o.updatedAt) : criadoEm
        // Faltantes vêm calculados do backend (mesma regra do bloqueio de envio);
        // o total sai do catálogo, também do backend.
        const faltantes = (o as { documentosObrigatoriosFaltantes?: number })
          .documentosObrigatoriosFaltantes ?? 0
        // `catalogo` (local), não o state: setState é assíncrono e o state ainda
        // está vazio nesta primeira passagem.
        const totalObrigatorios = (catalogo[o.tipoNecessidade ?? ''] ?? []).length
        // Modelo reserva-primeiro: prefere a identidade gravada na própria reserva
        // (cnpj/razão social), com fallback para o cadastro de cliente vinculado.
        const cnpjBruto = o.cnpj ?? cliente?.cnpj ?? null
        const consultor = resolverConsultor(o.usuarioResponsavel, o.consultorNome)
        return {
          id: o.id,
          numero: o.numero,
          clienteId: (o.clienteId as unknown as string) ?? undefined,
          data: criadoEm ? criadoEm.toLocaleDateString('pt-BR') : '—',
          ultimaMovimentacao: movidoEm ? movidoEm.toLocaleDateString('pt-BR') : '—',
          empresa: o.razaoSocial ?? cliente?.razaoSocial ?? `Cliente ${String(o.clienteId).slice(0, 8)}…`,
          cnpj: cnpjBruto ? formatCNPJ(cnpjBruto) : '—',
          consultorId: consultor.id,
          consultor: consultor.nome,
          produto: o.tipoNecessidade ?? '—',
          valor: Number(o.valorEstimado ?? 0),
          etapa: meta.etapa,
          status: meta.status,
          prazoReserva: o.prazoReserva ?? null,
          prazoOperacionalizacao: o.prazoOperacionalizacao ?? null,
          statusReserva: (o as { status?: string }).status ?? null,
          renovacaoAutomaticaUsadaEm: o.renovacaoAutomaticaUsadaEm ?? null,
          aderencia: o.scoreAderencia ?? 0,
          proximaAcao: o.proximaAcao ?? null,
          etapaComercial: o.etapaComercial ?? null,
          docsCount: docsPorOportunidade.get(o.id) ?? 0,
          obrigatoriosFaltantes: faltantes,
          obrigatoriosTotal: totalObrigatorios,
          reuniaoSolicitadaEm: o.reuniaoSolicitadaEm ?? null,
          reuniaoSolicitadaObservacao: o.reuniaoSolicitadaObservacao ?? null,
          dataPrimeiroContato: o.dataPrimeiroContato ?? null,
        }
      })
      setReservas(rows)
    } catch (e) {
      const falha = e as { message?: string; status?: number } | null
      setErroCarregar(falha?.message ?? 'Falha ao carregar reservas')
      setErroCarregarStatus(falha?.status ?? null)
      setReservas([])
    } finally {
      setLoading(false)
    }
  }, [usuario?.id, usuario?.nome])

  useEffect(() => {
    carregar()
  }, [carregar])
  // `?consultorId=` (Visão de Carteira → "Abrir →" na comparação por consultor, A05):
  // pré-filtra a lista pelo consultor; o chip limpa. Só faz sentido no back-office — para o
  // consultor a lista já vem no escopo dele e o filtro não remove nada.
  const consultorIdFiltro = searchParams.get('consultorId')
  const limparConsultorFiltro = () => {
    searchParams.delete('consultorId')
    setSearchParams(searchParams, { replace: true })
  }
  const nomeConsultorFiltro = useMemo(
    () => reservas.find((r) => r.consultorId === consultorIdFiltro)?.consultor ?? null,
    [reservas, consultorIdFiltro],
  )

  const filtered = useMemo(() => {
    const termo = search.toLowerCase()
    return reservas.filter(
      (r) =>
        (!consultorIdFiltro || r.consultorId === consultorIdFiltro) &&
        (vigencia === 'todas' || classificarVigencia(r) === vigencia) &&
        (situacao === 'TODAS' || r.etapaComercial === situacao) &&
        (r.empresa.toLowerCase().includes(termo) ||
          cnpjContemTermo(r.cnpj, search) ||
          r.consultor.toLowerCase().includes(termo) ||
          formatNumeroReserva(r.numero).toLowerCase().includes(termo))
    )
  }, [reservas, search, vigencia, situacao, consultorIdFiltro])

  /**
   * KPIs (Figma "Minhas Reservas"). A listagem da tabela É paginada no servidor
   * (`OportunidadeController`, page&size), então nada aqui conta sobre as linhas carregadas:
   * os quatro números são os de `GET /v1/oportunidades/resumo` — Ativas (etapa fora de
   * GANHO/PERDIDO, a mesma regra do Dashboard), A vencer (janela automática de renovação,
   * a mesma de `classificarVigencia`), Em estruturação e Ganhas. Sem resumo → "—".
   */
  const kpis = useMemo(
    () => ({
      ativas: resumoReservas?.ativas ?? null,
      aVencer: resumoReservas?.aVencer ?? null,
      emEstruturacao: resumoReservas?.emEstruturacao ?? null,
      ganhas: resumoReservas?.ganhas ?? null,
    }),
    [resumoReservas]
  )

  // Exporta o que está na tela (já filtrado e no escopo do usuário) — Fase 6, item 22.
  const exportarCsv = () => {
    baixarCsv(`reservas-${sufixoData()}`,
      ['Nº', 'Data', 'Empresa', 'CNPJ', 'Consultor', 'Produto', 'Valor', 'Etapa', 'Status',
        'Prazo', 'Aderência', 'Próxima ação'],
      filtered.map((r) => [
        formatNumeroReserva(r.numero), r.data, r.empresa, r.cnpj, r.consultor, r.produto,
        r.valor, r.etapa, r.status, prazoVigente(r).prazoISO ?? '', r.aderencia, r.proximaAcao ?? '',
      ]))
  }

  const handleAgendarReuniao = (r: ReservaRow) => {
    setReuniaoPrefill({
      oportunidadeId: r.id,
      clienteId: r.clienteId,
      clienteNome: r.empresa,
      titulo: `Reunião — ${r.empresa}`,
      // Sem primeiro contato ainda, esta é a primeira reunião: marcada assim, realizá-la
      // carimba o primeiro contato e encerra o pedido da Originei (revisão de 16/10/2026).
      primeiroContato: !r.dataPrimeiroContato,
    })
    setReuniaoDialogOpen(true)
  }

  const podeRenovar = useMemo(
    () => (usuario?.permissoes ?? []).some((p) => ROLES_RENOVACAO.includes(p)),
    [usuario?.permissoes],
  )

  const podeTransferir = useMemo(
    () => (usuario?.permissoes ?? []).some((p) => ROLES_TRANSFERENCIA.includes(p)),
    [usuario?.permissoes],
  )

  const abrirTransferencia = (r: ReservaRow) => {
    setTransferirReserva(r)
    setTransferirDestino('')
    setTransferirMotivo('')
    setTransferirErro(null)
  }

  const confirmarTransferencia = async () => {
    if (!transferirReserva || !transferirDestino) return
    setTransferindo(true)
    setTransferirErro(null)
    try {
      const nomeDestino = consultores.find((c) => c.id === transferirDestino)?.nome ?? '—'
      await oportunidadeService.transferir(transferirReserva.id, {
        novoConsultorId: transferirDestino,
        motivo: transferirMotivo.trim(),
        // Gravado na reserva (V49): o back-office passa a ver o nome sem o diretório do ADMIN.
        novoConsultorNome: nomeDestino !== '—' ? nomeDestino : null,
      })
      // Atualiza só a linha afetada, como nas demais ações do grid.
      setReservas((prev) =>
        prev.map((r) =>
          r.id === transferirReserva.id
            ? { ...r, consultorId: transferirDestino, consultor: nomeDestino }
            : r,
        ),
      )
      enqueueSnackbar(`Reserva transferida para ${nomeDestino}`, { variant: 'success' })
      setTransferirReserva(null)
    } catch (e) {
      setTransferirErro(
        e instanceof Error ? e.message : 'Não foi possível transferir a reserva',
      )
    } finally {
      setTransferindo(false)
    }
  }

  /**
   * O papel e o histórico definem o modo: a Originei renova direto; o consultor, na
   * PRIMEIRA vez (Handoff v4.0), ganha a renovação automática de exatamente +30 dias — e
   * nas seguintes solicita e aguarda a análise. O backend é a autoridade: se o privilégio
   * tiver sido consumido em outra aba, a resposta volta como solicitação pendente.
   */
  const abrirRenovacao = (r: ReservaRow) => {
    const base = r.prazoReserva ? new Date(`${r.prazoReserva}T00:00:00`) : new Date()
    const modo = podeRenovar
      ? 'direta'
      : (r.renovacaoAutomaticaUsadaEm == null ? 'automatica' : 'solicitacao')
    // Automática concede +30 exatos (o campo nem aparece); os demais modos sugerem +60.
    base.setDate(base.getDate() + (modo === 'automatica' ? 30 : 60))
    setRenovarReserva(r)
    setRenovarModo(modo)
    setRenovarPrazo(base.toISOString().slice(0, 10))
    setRenovarMotivo('')
    setRenovarErro(null)
  }

  const fecharRenovacao = () => {
    setRenovarReserva(null)
    setRenovarErro(null)
  }

  const confirmarRenovacao = async () => {
    if (!renovarReserva || !usuario?.id) return
    setRenovando(true)
    setRenovarErro(null)
    try {
      if (renovarModo === 'solicitacao' || renovarModo === 'automatica') {
        const solicitacao = await renovacaoService.solicitar({
          oportunidadeId: renovarReserva.id,
          novoPrazo: renovarPrazo,
          justificativa: renovarMotivo.trim(),
        })
        // A resposta diz o que aconteceu de verdade — o backend é quem decide se o pedido
        // era a renovação automática (+30, aprovada na hora) ou virou análise da Originei.
        if (solicitacao.status === 'APROVADA' && solicitacao.prazoAprovado) {
          const concedido = solicitacao.prazoAprovado
          setReservas((prev) =>
            prev.map((r) => (r.id === renovarReserva.id
              ? { ...r, prazoReserva: concedido, renovacaoAutomaticaUsadaEm: new Date().toISOString() }
              : r)),
          )
          enqueueSnackbar(`Prazo renovado automaticamente até ${formatDate(concedido)}`, {
            variant: 'success',
          })
        } else {
          enqueueSnackbar('Solicitação enviada — aguarde a análise da Originei', {
            variant: 'success',
          })
        }
        setRenovarReserva(null)
        return
      }

      const atualizada = await oportunidadeService.renovar(renovarReserva.id, {
        novoPrazo: renovarPrazo,
        motivo: renovarMotivo.trim() || undefined,
        usuarioId: usuario.id,
      })
      const novoPrazo = atualizada?.prazoReserva ?? renovarPrazo
      // Atualiza só a linha afetada — evita recarregar as 4 chamadas de `carregar`.
      setReservas((prev) =>
        prev.map((r) => (r.id === renovarReserva.id ? { ...r, prazoReserva: novoPrazo } : r)),
      )
      setDetailReserva((prev) =>
        prev && prev.id === renovarReserva.id ? { ...prev, prazoReserva: novoPrazo } : prev,
      )
      enqueueSnackbar(`Prazo renovado para ${formatDate(novoPrazo)}`, { variant: 'success' })
      setRenovarReserva(null)
    } catch (e) {
      const fallback =
        renovarModo === 'solicitacao'
          ? 'Não foi possível enviar a solicitação'
          : 'Não foi possível renovar o prazo'
      setRenovarErro(e instanceof Error ? e.message : fallback)
    } finally {
      setRenovando(false)
    }
  }

  /**
   * Colunas extras visíveis. "Consultor" começa ligada para quem enxerga mais de uma
   * carteira (ADMIN/GERENTE/OPERADOR) — para o consultor a coluna só repetiria o próprio nome.
   */
  /** Perfil de consultor — recebe o aviso dos pedidos de reunião da Originei. */
  const ehConsultor = (usuario?.permissoes ?? []).some((p) => p === 'CONSULTOR' || p === 'CONSULTOR_ONBOARDING')
  const [colunasExtras, setColunasExtras] = useState<ColunaExtra[]>(() => (ehConsultor ? [] : ['consultor']))
  const alternarColuna = (id: ColunaExtra) =>
    setColunasExtras((atual) => (atual.includes(id) ? atual.filter((c) => c !== id) : [...atual, id]))

  /** Cinco colunas do Figma + extras opcionais + ações rápidas; `Abrir →` é da TabelaAcao. */
  const colunas = useMemo<ColunaTabelaAcao<ReservaRow>[]>(() => {
    const base: ColunaTabelaAcao<ReservaRow>[] = [
      {
        titulo: 'Empresa / CNPJ',
        render: (r) => (
          <Box>
            <Typography component="strong" sx={{ fontWeight: 600, display: 'block' }}>{r.empresa}</Typography>
            <Typography variant="caption" sx={{ color: tokens.textSecondary, display: 'block' }}>
              <span>{formatNumeroReserva(r.numero)}</span>
              {' · '}
              <span>{r.cnpj}</span>
            </Typography>
            {/* Pedido de reunião do back-office (entrega G): destaque até a reunião acontecer. */}
            {reuniaoSolicitadaPendente(r) && (
              <Tooltip title={textoSolicitacaoReuniao(r)}>
                <Chip
                  size="small"
                  color="info"
                  icon={<EventIcon />}
                  label="Reunião solicitada pela Originei"
                  sx={{ mt: 0.5, height: 22, fontSize: '0.6875rem' }}
                />
              </Tooltip>
            )}
            {/* Próxima ação (Fase 6, item 18) como linha de apoio da empresa. */}
            {r.proximaAcao && (
              <Typography
                variant="caption"
                title={r.proximaAcao}
                sx={{
                  display: 'block',
                  color: tokens.brandPrimary,
                  maxWidth: 260,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                ▸ {r.proximaAcao}
              </Typography>
            )}
          </Box>
        ),
      },
      { titulo: 'Produto', render: (r) => r.produto },
      {
        // Etapa comercial (6.4): as seis do handoff, derivadas no backend. O "Etapa 1/2/3"
        // legado só aparece se o backend ainda não mandar a etapa.
        titulo: 'Situação',
        // Rascunho é a exceção da etapa comercial (09/09): o consultor "concluiu depois" e
        // precisa reconhecer a reserva para retomar — "Reserva" não diz isso.
        render: (r) => r.statusReserva === 'RASCUNHO'
          ? <Chip label="Rascunho" size="small" color="warning" variant="outlined" />
          : r.etapaComercial
          ? <Chip label={ETAPA_COMERCIAL_LABEL[r.etapaComercial]} size="small" color={etapaComercialChipColor(r.etapaComercial)} />
          : <Chip label={r.etapa} size="small" color={etapaColor(r.etapa) as any} />,
      },
      { titulo: 'Última movimentação', render: (r) => r.ultimaMovimentacao },
      {
        titulo: 'Validade',
        render: (r) => {
          const v = prazoVigente(r)
          const p = prazoInfo(v.prazoISO, !v.alertavel, v.operacionalizacao)
          const dias = v.alertavel ? diasAteOPrazo(v.prazoISO) : null
          const legenda = dias === null ? null
            : dias < 0 ? `vencida há ${Math.abs(dias)} dia(s)`
            : dias === 0 ? 'vence hoje'
            : `${dias} dia(s)`
          // Reserva contratada não tem prazo a renovar. A Originei renova direto; o
          // consultor solicita e aguarda a análise. Só nos últimos dias — `ofereceRenovacao`.
          const renovavel = r.etapa !== 'Etapa 3' && ofereceRenovacao(v.prazoISO, podeRenovar)
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
              <Tooltip title={p.tooltip}>
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: p.color === 'default' ? 400 : 600,
                      color: p.color === 'error' ? tokens.statusDanger
                        : p.color === 'warning' ? tokens.statusWarning
                        : 'inherit',
                    }}
                  >
                    {p.label}
                  </Typography>
                  {legenda && (
                    <Typography component="span" variant="caption" sx={{ color: tokens.textSecondary }}>
                      {legenda}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              {renovavel && (
                <Tooltip title={podeRenovar ? 'Renovar prazo da reserva' : 'Solicitar renovação de prazo'}>
                  <Button size="small" variant="outlined" color="primary" onClick={() => abrirRenovacao(r)}>
                    {podeRenovar ? 'Renovar' : 'Solicitar'}
                  </Button>
                </Tooltip>
              )}
            </Box>
          )
        },
      },
    ]
    const extras: Record<ColunaExtra, ColunaTabelaAcao<ReservaRow>> = {
      consultor: { titulo: 'Consultor', render: (r) => r.consultor },
      valor: { titulo: 'Valor', render: (r) => `R$ ${r.valor.toLocaleString('pt-BR')}`, align: 'right' },
      docs: {
        titulo: 'Documentos',
        render: (r) => (
          <Chip
            icon={<DescriptionIcon sx={{ fontSize: 14 }} />}
            label={r.docsCount}
            size="small"
            variant="outlined"
            color={r.docsCount > 0 ? 'primary' : 'default'}
          />
        ),
      },
      aderencia: {
        titulo: 'Aderência',
        render: (r) => (
          <Chip label={r.aderencia} size="small" color={r.aderencia >= 70 ? 'success' : r.aderencia >= 50 ? 'warning' : 'default'} />
        ),
      },
      criadaEm: { titulo: 'Criada em', render: (r) => r.data },
    }
    const acoes: ColunaTabelaAcao<ReservaRow> = {
      titulo: 'Ações',
      align: 'right',
      render: (r) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {/* Rascunho: o upload dos pendentes (e o envio para aprovação) é a ação
              principal da linha — botão com texto, não só ícone (revisão de 06/09). */}
          {r.statusReserva === 'RASCUNHO' ? (
            <Tooltip
              title={
                r.obrigatoriosFaltantes > 0
                  ? `Continuar o cadastro: ${r.obrigatoriosFaltantes} documento(s) obrigatório(s) pendente(s)`
                  : 'Continuar o cadastro: documentos completos — envie a reserva para aprovação'
              }
            >
              {/* 10/09: rascunho reabre o wizard na etapa de documentos (retomada), não só
                  o diálogo de upload — é o mesmo caminho do "Concluir depois", de volta. */}
              <Button
                size="small"
                variant="outlined"
                color={r.obrigatoriosFaltantes > 0 ? 'warning' : 'success'}
                startIcon={<FolderOpenIcon fontSize="small" />}
                onClick={() => navigate(`/reservas/nova?continuar=${r.id}`)}
                sx={{ mr: 0.5 }}
              >
                {r.obrigatoriosFaltantes > 0
                  ? `Documentos (${r.obrigatoriosFaltantes})`
                  : 'Enviar p/ aprovação'}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                r.obrigatoriosFaltantes > 0
                  ? `${r.obrigatoriosFaltantes} de ${r.obrigatoriosTotal} documento(s) obrigatório(s) pendente(s)`
                  : 'Documentos obrigatórios completos'
              }
            >
              <IconButton size="small" onClick={() => setDocsDialogReserva(r)}>
                <FolderOpenIcon fontSize="small" color={r.obrigatoriosFaltantes > 0 ? 'warning' : 'success'} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Pendências da reserva">
            <IconButton size="small" onClick={() => navigate(`/reservas/${r.id}/pendencias`)}>
              <AssignmentLateIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
          {/* Reunião só depois da aprovação da Originei (06/09) — o backend recusa antes
              (RESERVA_NAO_APROVADA); aqui o botão fica desabilitado com o motivo. */}
          <Tooltip
            title={
              !permiteAgendamento(r.statusReserva)
                ? MOTIVO_AGENDAMENTO_INDISPONIVEL
                : reuniaoSolicitadaPendente(r)
                  ? 'A Originei solicitou esta reunião — agendar'
                  : 'Agendar reunião'
            }
          >
            <span>
              <IconButton
                size="small"
                aria-label="Agendar reunião"
                onClick={() => handleAgendarReuniao(r)}
                disabled={!permiteAgendamento(r.statusReserva)}
              >
                <Badge
                  variant="dot"
                  color="warning"
                  invisible={!reuniaoSolicitadaPendente(r) || !permiteAgendamento(r.statusReserva)}
                >
                  <EventIcon
                    fontSize="small"
                    color={permiteAgendamento(r.statusReserva) ? 'primary' : 'disabled'}
                  />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
          {podeTransferir && (
            <Tooltip title="Transferir reserva de carteira">
              <IconButton size="small" onClick={() => abrirTransferencia(r)}>
                <SwapHorizIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    }
    return [...base, ...COLUNAS_EXTRAS.filter((c) => colunasExtras.includes(c.id)).map((c) => extras[c.id]), acoes]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colunasExtras, podeRenovar, podeTransferir])

  const openDetail = useCallback(async (reserva: ReservaRow) => {
    setDetailReserva(reserva)
    setDetailDocs([])
    setDetailErroDocs(null)
    setDetailLoadingDocs(true)
    try {
      const page = await documentoService.listarFiltrado(
        TIPO_ENTIDADE_OPORTUNIDADE,
        reserva.id,
        0,
        200
      )
      setDetailDocs(page.content ?? [])
    } catch (e) {
      setDetailErroDocs(
        e instanceof Error ? e.message : 'Não foi possível carregar os documentos'
      )
    } finally {
      setDetailLoadingDocs(false)
    }
  }, [])

  const closeDetail = () => {
    setDetailReserva(null)
    setDetailDocs([])
    setDetailErroDocs(null)
  }

  // Visualiza (download=false) ou baixa (download=true) o arquivo de um documento
  const abrirArquivo = useCallback(
    async (doc: Documento, download: boolean) => {
      setDocAcaoId(doc.id)
      try {
        const blob = await documentoService.baixarArquivo(doc.id)
        const url = URL.createObjectURL(blob)
        if (download) {
          const a = document.createElement('a')
          a.href = url
          a.download = doc.nome
          document.body.appendChild(a)
          a.click()
          a.remove()
        } else {
          window.open(url, '_blank', 'noopener')
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      } catch (e) {
        enqueueSnackbar(
          e instanceof Error ? e.message : 'Falha ao abrir o documento',
          { variant: 'error' }
        )
      } finally {
        setDocAcaoId(null)
      }
    },
    [enqueueSnackbar]
  )

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  return (
    <Box>
      <PageHeader
        title="Minhas Reservas"
        subtitle="Acompanhe a validade e a situação de cada reserva"
        action={
          // Porta única de criação: a página /reservas/nova (A07). O modal antigo "Nova
          // Reserva" e o retorno por query string saíram na Onda 8 / A16 — o cadastro de cliente
          // volta para /reservas/nova?cnpj=.
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/reservas/nova')}>
            Nova oportunidade
          </Button>
        }
      />

      {/* KPIs — fonte: GET /v1/oportunidades/resumo (ver `kpis`); nulo/erro vira "—". */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Ativas"
            valor={erroCarregar ? '—' : (kpis.ativas ?? '—')}
            legenda="No funil (Reserva a Negociação)"
            variante="escura"
            carregando={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="A vencer"
            valor={erroCarregar ? '—' : (kpis.aVencer ?? '—')}
            legenda={`Validade em até ${JANELA_RENOVACAO_DIAS} dias`}
            carregando={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Em estruturação"
            valor={erroCarregar ? '—' : (kpis.emEstruturacao ?? '—')}
            legenda="Em estruturação pela Originei"
            carregando={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Ganhas"
            valor={erroCarregar ? '—' : (kpis.ganhas ?? '—')}
            legenda="Contrato assinado ou operação ativa"
            carregando={loading}
          />
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2 }} elevation={0}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar por empresa, CNPJ, consultor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 240, maxWidth: 400 }}
          />
          {consultorIdFiltro && (
            <Chip
              label={`Consultor: ${nomeConsultorFiltro ?? `${consultorIdFiltro.slice(0, 8)}…`}`}
              onDelete={limparConsultorFiltro}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          {/* Situação = etapa comercial derivada (6.4); nunca o status gravado. */}
          <TextField
            select
            size="small"
            label="Situação"
            value={situacao}
            onChange={(e) => setSituacao(e.target.value as FiltroSituacao)}
            sx={{ minWidth: 200 }}
            inputProps={{ 'aria-label': 'Filtrar por situação' }}
          >
            <MenuItem value="TODAS">Todas as etapas</MenuItem>
            {ETAPAS_COMERCIAIS.map((etapa) => (
              <MenuItem key={etapa} value={etapa}>{ETAPA_COMERCIAL_LABEL[etapa]}</MenuItem>
            ))}
          </TextField>
          {/* "Período" do Figma = vigência (Fase 6, item 19): a janela é a mesma do botão Renovar. */}
          <TextField
            select
            size="small"
            label="Vigência"
            value={vigencia}
            onChange={(e) => setVigencia(e.target.value as FiltroVigencia)}
            sx={{ minWidth: 260 }}
            inputProps={{ 'aria-label': 'Filtrar por vigência' }}
          >
            {(Object.keys(ROTULO_VIGENCIA) as FiltroVigencia[]).map((v) => (
              <MenuItem key={v} value={v}>{ROTULO_VIGENCIA[v]}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          {/* CSV e colunas extras saem da barra e vão para o menu `⋯` (Figma). */}
          <Tooltip title="Mais opções">
            <IconButton
              aria-label="Mais opções"
              aria-haspopup="menu"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              disabled={filtered.length === 0}
              onClick={() => { setMenuAnchor(null); exportarCsv() }}
            >
              Exportar CSV
            </MenuItem>
            <Divider />
            <Typography variant="caption" sx={{ px: 2, py: 0.5, color: tokens.textSecondary, display: 'block' }}>
              Colunas extras
            </Typography>
            {COLUNAS_EXTRAS.map((c) => (
              <MenuItem key={c.id} dense onClick={() => alternarColuna(c.id)}>
                <Checkbox
                  size="small"
                  edge="start"
                  checked={colunasExtras.includes(c.id)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-label': `Coluna ${c.rotulo}` }}
                />
                {c.rotulo}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Consultor: aviso de que a Originei pediu reunião em reservas da carteira (entrega G). */}
        {!loading && !erroCarregar && ehConsultor && reservas.some(reuniaoSolicitadaPendente) && (
          <Alert severity="info" icon={<EventIcon />} sx={{ mx: 2, mb: 2 }}>
            A Originei solicitou reunião em algumas reservas — elas estão destacadas na lista.
          </Alert>
        )}

        {loading ? (
          <EstadoDeLista estado="carregando" mensagem="Carregando reservas…" />
        ) : erroCarregar ? (
          <EstadoDeLista
            estado={erroCarregarStatus === 403 ? 'semPermissao' : 'erro'}
            mensagem={erroCarregarStatus === 403 ? undefined : 'Não foi possível carregar as reservas'}
            detalhe={erroCarregarStatus === 403 ? undefined : erroCarregar}
            acao={erroCarregarStatus === 403 ? undefined : { label: 'Tentar novamente', onClick: () => void carregar() }}
          />
        ) : reservas.length === 0 ? (
          <EstadoDeLista
            estado="vazio"
            mensagem="Nenhuma reserva cadastrada"
            detalhe='Clique em "Nova oportunidade" para começar.'
            acao={{ label: 'Criar a primeira reserva', onClick: () => navigate('/reservas/nova') }}
          />
        ) : filtered.length === 0 ? (
          <EstadoDeLista
            estado="vazio"
            mensagem="Nenhuma reserva com esses filtros"
            detalhe="Ajuste a busca, a situação ou a vigência."
          />
        ) : (
          <TabelaAcao<ReservaRow>
            aria-label="Minhas reservas"
            colunas={colunas}
            linhas={filtered}
            chaveDaLinha={(r) => r.id}
            onAbrir={(r) => void openDetail(r)}
          />
        )}
      </Paper>

      {/* ── MODAL: Detalhes da Reserva ── */}
      <Dialog open={!!detailReserva} onClose={closeDetail} maxWidth="md" fullWidth>
        {detailReserva && (
          <>
            <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
              {detailReserva.empresa}
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, mt: 0.5 }}
              >
                {detailReserva.numero != null
                  ? formatNumeroReserva(detailReserva.numero)
                  : `Reserva #${detailReserva.id.slice(0, 8)}`}{' '}
                · criada em {detailReserva.data}
              </Typography>
            </DialogTitle>

            <DialogContent>
              <Box sx={{ mt: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={detailReserva.etapa}
                    size="small"
                    color={etapaColor(detailReserva.etapa) as any}
                  />
                  <Chip
                    label={detailReserva.status}
                    size="small"
                    color={statusColor(detailReserva.status) as any}
                  />
                  <Chip
                    label={`Aderência ${detailReserva.aderencia}/100`}
                    size="small"
                    color={
                      detailReserva.aderencia >= 70
                        ? 'success'
                        : detailReserva.aderencia >= 50
                        ? 'warning'
                        : 'default'
                    }
                  />
                </Box>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      rowGap: 1.5,
                      columnGap: 2,
                    }}
                  >
                    {[
                      ['CNPJ', detailReserva.cnpj],
                      ['Consultor', detailReserva.consultor],
                      ['Produto', detailReserva.produto],
                      [
                        'Valor Estimado',
                        `R$ ${detailReserva.valor.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                        })}`,
                      ],
                      ['Prazo de fechamento', prazoInfo(detailReserva.prazoReserva, true).label],
                      // Só aparece depois da assinatura — antes disso não existe.
                      ...(detailReserva.prazoOperacionalizacao
                        ? [
                            [
                              'Prazo de operacionalização',
                              prazoInfo(detailReserva.prazoOperacionalizacao, true).label,
                            ] as [string, string],
                          ]
                        : []),
                      ['Etapa', detailReserva.etapa],
                      ['Status', detailReserva.status],
                    ].map(([label, value]) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Documentos vinculados ({detailReserva.docsCount})
              </Typography>

              {detailLoadingDocs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : detailErroDocs ? (
                <Alert severity="error">{detailErroDocs}</Alert>
              ) : detailDocs.length === 0 ? (
                <Alert severity="info">Nenhum documento anexado a esta reserva.</Alert>
              ) : (
                <List dense disablePadding>
                  {detailDocs.map((d) => (
                    <ListItem
                      key={d.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        mb: 0.75,
                        pr: 12,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <DescriptionIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={d.nome}
                        secondary={`${d.tipo} · ${formatFileSize(d.tamanhoBytes)} · ${d.status}`}
                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.875rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                      <ListItemSecondaryAction>
                        {docAcaoId === d.id ? (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ) : (
                          <>
                            <Tooltip title="Visualizar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => abrirArquivo(d, false)}
                                disabled={docAcaoId !== null}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Baixar">
                              <IconButton
                                size="small"
                                onClick={() => abrirArquivo(d, true)}
                                disabled={docAcaoId !== null}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeDetail}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── MODAL: Transferir reserva de carteira ── */}
      <Dialog
        open={!!transferirReserva}
        onClose={() => setTransferirReserva(null)}
        maxWidth="xs"
        fullWidth
      >
        {transferirReserva && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>Transferir de carteira</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {transferirReserva.empresa} · carteira atual{' '}
                <strong>{transferirReserva.consultor}</strong>
              </Typography>
              {transferirErro && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {transferirErro}
                </Alert>
              )}
              <TextField
                select
                label="Novo consultor"
                fullWidth
                value={transferirDestino}
                onChange={(e) => setTransferirDestino(e.target.value)}
                sx={{ mb: 2 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Selecione…</option>
                {consultores
                  .filter((c) => c.id !== transferirReserva.consultorId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
              </TextField>
              <TextField
                label="Motivo"
                required
                fullWidth
                multiline
                minRows={2}
                value={transferirMotivo}
                onChange={(e) => setTransferirMotivo(e.target.value)}
                placeholder="Ex.: consultor saiu da operação"
                helperText="Fica registrado no histórico de carteira da reserva."
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setTransferirReserva(null)} disabled={transferindo}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={confirmarTransferencia}
                disabled={transferindo || !transferirDestino || !transferirMotivo.trim()}
                startIcon={
                  transferindo ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {transferindo ? 'Transferindo…' : 'Transferir'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── MODAL: Renovar prazo da reserva ── */}
      <Dialog open={!!renovarReserva} onClose={fecharRenovacao} maxWidth="xs" fullWidth>
        {renovarReserva && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              {renovarModo === 'solicitacao' ? 'Solicitar renovação' : 'Renovar prazo'}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {renovarReserva.empresa} · prazo atual{' '}
                <strong>
                  {renovarReserva.prazoReserva ? formatDate(renovarReserva.prazoReserva) : '—'}
                </strong>
              </Typography>
              {renovarModo === 'solicitacao' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  A renovação depende de aprovação da Originei. O prazo só muda depois que a
                  solicitação for aprovada.
                </Alert>
              )}
              {renovarModo === 'automatica' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Sua <strong>primeira renovação é automática</strong>: o prazo será estendido
                  em exatamente <strong>30 dias</strong>
                  {renovarPrazo ? <> (até <strong>{formatDate(renovarPrazo)}</strong>)</> : null}.
                  As próximas dependerão de aprovação da Originei.
                </Alert>
              )}
              {renovarErro && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {renovarErro}
                </Alert>
              )}
              {/* No modo automático não há data a escolher — a regra concede +30 exatos. */}
              {renovarModo !== 'automatica' && (
                <TextField
                  label="Novo prazo"
                  type="date"
                  fullWidth
                  value={renovarPrazo}
                  onChange={(e) => setRenovarPrazo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  // Teto de 60 dias (RES-004). O backend recusa com PRAZO_EXCEDE_MAXIMO de
                  // qualquer forma; aqui o seletor já não deixa escolher a data inválida.
                  inputProps={{ max: limiteRenovacao(renovarReserva) ?? undefined }}
                  helperText="Posterior ao prazo atual, no máximo 60 dias adiante."
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                label={renovarModo === 'direta' ? 'Motivo (opcional)' : 'Justificativa'}
                required={renovarModo !== 'direta'}
                fullWidth
                multiline
                minRows={2}
                value={renovarMotivo}
                onChange={(e) => setRenovarMotivo(e.target.value)}
                placeholder="Ex.: cliente pediu mais prazo para reunir os documentos"
                helperText={
                  renovarModo === 'solicitacao'
                    ? 'É o que a Originei analisa para aprovar ou reprovar.'
                    : renovarModo === 'automatica'
                      ? 'Fica registrada no histórico da renovação.'
                      : undefined
                }
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={fecharRenovacao} disabled={renovando}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={confirmarRenovacao}
                disabled={
                  renovando ||
                  !renovarPrazo ||
                  (renovarModo !== 'direta' && !renovarMotivo.trim())
                }
                startIcon={renovando ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {renovando
                  ? (renovarModo === 'solicitacao' ? 'Enviando…' : 'Renovando…')
                  : renovarModo === 'solicitacao'
                    ? 'Solicitar'
                    : renovarModo === 'automatica'
                      ? 'Renovar (+30 dias)'
                      : 'Renovar'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── MODAL: Agendar reunião (quick-action) ── */}
      <ReuniaoDialog
        open={reuniaoDialogOpen}
        onClose={() => setReuniaoDialogOpen(false)}
        prefill={reuniaoPrefill}
      />

      {/* ── MODAL: Upload de documentos obrigatórios ── */}
      {docsDialogReserva && (
        <DocsObrigatoriosDialog
          open={true}
          onClose={() => setDocsDialogReserva(null)}
          reservaId={docsDialogReserva.id}
          produto={docsDialogReserva.produto}
          empresa={docsDialogReserva.empresa}
          statusReserva={docsDialogReserva.statusReserva}
          onUploadSucesso={carregar}
          onEnviado={carregar}
        />
      )}

    </Box>
  )
}

export default ReservasListPage
